import { useEffect, useState } from 'react'
import { isObject } from 'lodash'
import { GridRenderCellParams } from '@microservices/wiskey-react-table-component'

import { objectToOptions } from '@pages/ConfiguredEntity/helpers'
import { unsavedChangesRefType } from '@pages/ConfiguredEntity/types'

import { useExecuteCommandMutation } from '@redux/api'

import { getCommandRequestData, isNotEmpty } from '@helpers'
import { FIELD_VALUE_TYPE, OBJECT_FIELD_TYPE } from '@constants'
import { AutocompleteOption, GETField, ObjectDataRecord, Row } from '@types'

export const useDefaultViewEditValues = (
  params: GridRenderCellParams<unknown, ObjectDataRecord, unknown>,
  cellField: GETField | undefined,
  unsavedChangesRef: unsavedChangesRefType
) => {
  const isRefField =
    cellField?.valueType === FIELD_VALUE_TYPE.OBJ_PK_LINK ||
    cellField?.valueType === FIELD_VALUE_TYPE.OBJ_INTERNAL_ID_LINK

  const [objectPickerValue, setObjectPickerValue] = useState<
    AutocompleteOption | AutocompleteOption[] | null
  >(null)

  const COMMAND_CODE = 'object_read_by_id'

  const [executeCommand] = useExecuteCommandMutation()

  useEffect(() => {
    if (!isRefField) {
      return
    }
    const rowId = params.row._id || params.row.CONFIGURED_ENTITY_DATA_GRID_ROW_SYSTEM_UNIQUE_ID
    const field = params.field
    const columnValue = params.colDef.value
    const unsavedChanges = unsavedChangesRef.current
    const isMultiple = params.colDef.fieldType === OBJECT_FIELD_TYPE.ARRAY

    if (unsavedChanges.rows?.[rowId] && field in unsavedChanges.rows[rowId]) {
      if (isMultiple) {
        const options = Array.isArray(params.value)
          ? params.value
          : objectToOptions(params.value as Record<string, string>)

        setObjectPickerValue(options)

        return
      }

      if (!unsavedChanges.rows[rowId][field]) {
        setObjectPickerValue(unsavedChanges.rows[rowId][field])

        return
      }
    }

    if (isMultiple) {
      const propperValue = Array.isArray(params.value)
        ? params.value
        : objectToOptions(params.value as Record<string, string>)

      setObjectPickerValue(propperValue)

      if (params.row.isNew) return

      if (!unsavedChanges.originalRows[rowId]) {
        unsavedChanges.originalRows[rowId] = { ...params.row, [field]: propperValue }
      } else {
        unsavedChanges.originalRows[rowId][field] = propperValue
      }

      return
    }

    if (!isObject(params.value) && !params.row.isNew) {
      executeCommand({
        method: 'GET',
        objectCode: cellField?.objectCode,
        commandCode: COMMAND_CODE,
        params: getCommandRequestData([{ title: 'objectId', value: rowId }]),
      })
        .unwrap()
        .then((row: Row) => {
          const fieldId = row[field] || row[columnValue]
          const label = isNotEmpty(params.value) ? String(params.value) : ''

          if (!fieldId) return

          const newValue = { id: fieldId, label }
          setObjectPickerValue(newValue)
        })

      return
    }

    setObjectPickerValue(params.value)
  }, [])

  return objectPickerValue
}
